import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Car, LogOut, Loader2, RefreshCw, Users, LayoutGrid, ArrowLeft, Search, AlertTriangle, Camera, Gauge, Wallet, Download, Plus, FileInput, Sparkles, Trash2 } from "lucide-react";
import { useAuth } from "@/portal/AuthContext";
import { apiFetch, downloadFile } from "@/portal/api";
import { LOGO_IMG } from "@/portal/components/adminShared";
import { toast } from "sonner";
import FleetTable from "@/flotila/FleetTable";
import CarDetail from "@/flotila/CarDetail";
import PartnersPanel from "@/flotila/PartnersPanel";
import BulkPartnerModal from "@/flotila/BulkPartnerModal";
import AddCarModal from "@/flotila/AddCarModal";
import { confirmDialog } from "@/portal/confirm";
import { fmtMonth } from "@/flotila/fleetUtils";
import { resolveBrand, brandStyle, brandLogoSrc, EFFECTDEAL_BRAND } from "@/flotila/brand";
import "@/flotila/flotila.css";

const FlotilaPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role === "admin" || user?.role === "assistant";
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [view, setView] = useState("table"); // table | partners
  const [carId, setCarId] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | alerts | missing | rented | free
  const [bulkOpen, setBulkOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(null); // "import" | "dedupe" | "bulkdel" | "bulkstat"
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async () => {
    try { setData(await apiFetch("/flotila/overview", { token })); setSelected(new Set()); } catch (e) { toast.error(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const importFromContracts = async () => {
    setBusy("import");
    try {
      const r = await apiFetch("/admin/cars/import-from-contracts", { method: "POST", token });
      toast.success(`Hotovo: přidáno ${r.created} vozů, doplněno SPZ/VIN u ${r.updated}.`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const dedupe = async () => {
    setBusy("dedupe");
    try {
      const r = await apiFetch("/admin/cars/dedupe", { method: "POST", token });
      const parts = [];
      if (r.removed) parts.push(`${r.removed} duplicit/testovacích vozů`);
      if (r.removed_rentals) parts.push(`${r.removed_rentals} duplicitních pronájmů`);
      toast.success(parts.length ? `Uklizeno: odebráno ${parts.join(" a ")}.` : "Žádné duplicity k úklidu.");
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const brand = user?.role === "partner" ? resolveBrand(data?.brand || {}) : EFFECTDEAL_BRAND;
  const logo = brandLogoSrc(brand);
  const cars = data?.cars || [];
  const stats = {
    total: cars.length,
    alerts: cars.filter((c) => c.alerts?.length).length,
    missing: cars.filter((c) => !c.this_month?.mileage && !c.this_month?.photos?.length).length,
    photos: cars.reduce((s, c) => s + (c.this_month?.photos?.length || 0), 0),
  };
  const payoutTotal = cars.reduce((s, c) => s + (Number(c.partner_monthly) || 0), 0);
  const payoutCars = cars.filter((c) => Number(c.partner_monthly) > 0).length;
  const downloadStatement = async () => {
    try {
      await downloadFile(`/flotila/payout-statement?month=${data?.month || ""}`, token, `vyuctovani-odmeny-${data?.month || ""}.pdf`);
    } catch (e) { toast.error(e.message); }
  };
  const filtered = cars.filter((c) => {
    if (filter === "alerts" && !c.alerts?.length) return false;
    if (filter === "missing" && (c.this_month?.mileage || c.this_month?.photos?.length)) return false;
    if (filter === "rented" && c.status !== "rented" && !c.client_name) return false;
    if (filter === "free" && (c.status !== "free" || c.client_name)) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [c.brand, c.model, c.license_plate, c.vin, c.client_name, c.partner_name].some((x) => (x || "").toLowerCase().includes(s));
  });

  const handleLogout = () => { logout(); navigate("/prihlaseni"); };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (filtered.length && filtered.every((c) => s.has(c.id))) ? new Set() : new Set(filtered.map((c) => c.id)));
  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!(await confirmDialog({ title: `Smazat ${ids.length} vozů?`, description: "Vozy s aktivní smlouvou se přeskočí. Akci nelze vrátit.", confirmText: "Smazat", danger: true }))) return;
    setBusy("bulkdel");
    try {
      const r = await apiFetch("/admin/cars/bulk-delete", { method: "POST", token, body: { ids } });
      toast.success(`Smazáno ${r.deleted} vozů.${r.skipped?.length ? ` Přeskočeno (mají smlouvu): ${r.skipped.join(", ")}.` : ""}`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const bulkStatus = async (status) => {
    const ids = [...selected];
    if (!ids.length) return;
    setBusy("bulkstat");
    try {
      const r = await apiFetch("/admin/cars/bulk-status", { method: "POST", token, body: { ids, status } });
      toast.success(`Upraveno ${r.updated} vozů.`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen fl-root" data-fl-theme={brand.theme} style={brandStyle(brand)} data-testid="flotila-page" data-brand={brand.slug || "effectdeal"}>
      <header className="fl-header sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-testid="flotila-brand-header">
            {logo ? <img src={logo} alt={brand.company_name} className="h-9 max-w-[150px] object-contain" /> : <div className="w-9 h-9 rounded-xl fl-accent-bg flex items-center justify-center font-bold">{(brand.company_name || "F").slice(0, 1).toUpperCase()}</div>}
            <div>
              <span className="font-['Cabinet_Grotesk'] font-bold tracking-wide block leading-tight">{brand.is_default ? <>EFFECTDEAL <span className="fl-accent-text">FLOTILA</span></> : <>{brand.company_name} <span className="fl-accent-text">FLOTILA</span></>}</span>
              <span className="text-xs fl-muted">{brand.tagline || (user?.role === "partner" ? "Partnerský přehled vozů" : "Evidence vozového parku")}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {isStaff && <a href="/admin" className="hidden sm:inline text-sm fl-muted hover:opacity-80" data-testid="flotila-back-admin">← Administrace</a>}
            <span className="hidden md:inline text-sm fl-muted">{user?.name || user?.email}</span>
            <button onClick={handleLogout} className="text-sm fl-muted hover:opacity-80 inline-flex items-center gap-2" data-testid="flotila-logout"><LogOut className="w-4 h-4" /> Odhlásit</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {carId ? (
          <CarDetail carId={carId} token={token} isStaff={isStaff} isAdmin={isAdmin} onBack={() => { setCarId(null); load(); }} />
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <h1 className="font-['Cabinet_Grotesk'] text-2xl sm:text-3xl font-bold">{view === "partners" ? "Partneři" : "Vozový park"}</h1>
                <p className="text-sm fl-muted mt-1">{view === "partners" ? "Majitelé vozů s přístupem jen ke svým autům." : <>Aktuální měsíc: <span className="fl-text-2 capitalize">{fmtMonth(data?.month)}</span> · každý měsíc zapište nájezd a nahrajte fotky.</>}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {isStaff && (
                  <div className="inline-flex rounded-full border fl-border fl-surface-2 p-1">
                    <button onClick={() => setView("table")} className={`px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 ${view === "table" ? "fl-accent-bg" : "fl-muted hover:opacity-80"}`} data-testid="flotila-view-table"><LayoutGrid className="w-4 h-4" /> Tabulka</button>
                    {isAdmin && <button onClick={() => setView("partners")} className={`px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 ${view === "partners" ? "fl-accent-bg" : "fl-muted hover:opacity-80"}`} data-testid="flotila-view-partners"><Users className="w-4 h-4" /> Partneři</button>}
                  </div>
                )}
                {isAdmin && view === "table" && <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full fl-accent-bg font-medium" data-testid="flotila-add-car"><Plus className="w-4 h-4" /> Přidat vůz</button>}
                {isAdmin && view === "table" && <button onClick={importFromContracts} disabled={busy === "import"} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border fl-border fl-text-2 hover:opacity-80 disabled:opacity-60" title="Doplní auta a jejich SPZ/VIN ze smluv (pronajatých vozů)" data-testid="flotila-import-contracts">{busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileInput className="w-4 h-4" />} Doplnit ze smluv</button>}
                {isAdmin && view === "table" && <button onClick={dedupe} disabled={busy === "dedupe"} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border fl-border fl-text-2 hover:opacity-80 disabled:opacity-60" title="Sloučí duplicity a odstraní testovací/osiřelé vozy (auta s vlastní SPZ/VIN nikdy nemaže)" data-testid="flotila-dedupe">{busy === "dedupe" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Uklidit duplicity</button>}
                {isAdmin && view === "table" && <button onClick={() => setBulkOpen(true)} className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border fl-border fl-text-2 hover:opacity-80" data-testid="flotila-bulk-partner"><Users className="w-4 h-4" /> Přiřadit partnera</button>}
                <button onClick={load} className="p-2 rounded-full border fl-border fl-muted hover:opacity-80" title="Obnovit" data-testid="flotila-refresh"><RefreshCw className="w-4 h-4" /></button>
              </div>
            </div>

            {view === "partners" ? (
              <PartnersPanel token={token} cars={cars} onChanged={load} />
            ) : !data ? (
              <div className="py-24 text-center fl-muted"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : (
              <>
                {!isStaff && payoutTotal > 0 && (
                  <div className="fl-card rounded-2xl p-5 mb-5 fl-accent-ring flex flex-wrap items-center justify-between gap-4" data-testid="flotila-payout-summary">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl fl-accent-bg flex items-center justify-center shrink-0"><Wallet className="w-5 h-5" /></div>
                      <div>
                        <p className="text-xs fl-muted uppercase tracking-wider">Tento měsíc vám pošleme celkem</p>
                        <p className="text-sm fl-muted mt-0.5">za {payoutCars} {payoutCars === 1 ? "vůz" : payoutCars < 5 ? "vozy" : "vozů"}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold font-['Cabinet_Grotesk'] fl-accent-text" data-testid="flotila-payout-total">{Math.round(payoutTotal).toLocaleString("cs-CZ")} Kč</p>
                      <p className="text-xs fl-muted mt-0.5">{Math.round(payoutTotal / 1.21).toLocaleString("cs-CZ")} Kč bez DPH</p>
                      <button onClick={downloadStatement} className="mt-2 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border fl-border fl-muted hover:opacity-80 transition-opacity" data-testid="flotila-payout-download"><Download className="w-3.5 h-3.5" /> Stáhnout vyúčtování (PDF)</button>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="flotila-stats">
                  {[
                    { k: "all", icon: Car, label: "Vozů celkem", v: stats.total, cls: "fl-text-2" },
                    { k: "alerts", icon: AlertTriangle, label: "S upozorněním", v: stats.alerts, cls: stats.alerts ? "fl-bad" : "fl-ok" },
                    { k: "missing", icon: Gauge, label: "Chybí měsíční zápis", v: stats.missing, cls: stats.missing ? "fl-warn" : "fl-ok" },
                    { k: "photos", icon: Camera, label: "Fotek tento měsíc", v: stats.photos, cls: "fl-text-2" },
                  ].map((s) => (
                    <button key={s.k} onClick={() => s.k !== "photos" && setFilter(s.k)} className={`fl-card rounded-2xl p-4 text-left transition-colors ${filter === s.k ? "fl-accent-ring" : "fl-row"}`} data-testid={`flotila-stat-${s.k}`}>
                      <div className="flex items-center gap-2 text-xs fl-muted mb-1"><s.icon className="w-3.5 h-3.5" /> {s.label}</div>
                      <p className={`text-2xl font-bold font-['Cabinet_Grotesk'] ${s.cls}`}>{s.v}</p>
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 fl-muted" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat vůz, SPZ, VIN, klienta…" className="fl-input !pl-9 w-full" data-testid="flotila-search" />
                  </div>
                  {[["all", "Vše"], ["alerts", "Upozornění"], ["missing", "Bez zápisu"], ["rented", "Pronajaté"], ["free", "Volné"]].map(([k, l]) => (
                    <button key={k} onClick={() => setFilter(k)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "fl-accent-soft fl-accent-border" : "fl-border fl-muted hover:opacity-80"}`} data-testid={`flotila-filter-${k}`}>{l}</button>
                  ))}
                  <span className="text-xs fl-muted ml-auto">{filtered.length} / {cars.length} vozů</span>
                </div>

                {isAdmin && selected.size > 0 && (
                  <div className="fl-card rounded-xl px-4 py-3 mb-4 flex flex-wrap items-center gap-3 fl-accent-ring" data-testid="flotila-bulk-bar">
                    <span className="text-sm font-medium fl-text-2">Vybráno {selected.size}</span>
                    <div className="h-4 w-px fl-divider" />
                    <button onClick={() => bulkStatus("free")} disabled={busy === "bulkstat"} className="text-xs px-3 py-1.5 rounded-full border fl-border fl-text-2 hover:opacity-80 disabled:opacity-60" data-testid="bulk-status-free">Označit jako volné</button>
                    <button onClick={() => bulkStatus("service")} disabled={busy === "bulkstat"} className="text-xs px-3 py-1.5 rounded-full border fl-border fl-text-2 hover:opacity-80 disabled:opacity-60" data-testid="bulk-status-service">Dát do servisu</button>
                    <button onClick={() => setBulkOpen(true)} className="text-xs px-3 py-1.5 rounded-full border fl-border fl-text-2 hover:opacity-80 inline-flex items-center gap-1.5" data-testid="bulk-assign-partner"><Users className="w-3.5 h-3.5" /> Přiřadit partnera</button>
                    <button onClick={bulkDelete} disabled={busy === "bulkdel"} className="text-xs px-3 py-1.5 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5 disabled:opacity-60 ml-auto" data-testid="bulk-delete">{busy === "bulkdel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Smazat vybrané</button>
                    <button onClick={() => setSelected(new Set())} className="text-xs fl-muted hover:opacity-80" data-testid="bulk-clear">Zrušit výběr</button>
                  </div>
                )}

                <FleetTable cars={filtered} month={data.month} token={token} isStaff={isStaff} onOpen={setCarId} selectable={isAdmin} selectedIds={selected} onToggle={toggleSel} onToggleAll={toggleAll} />
              </>
            )}
          </>
        )}
      </main>
      {bulkOpen && <BulkPartnerModal cars={selected.size ? cars.filter((c) => selected.has(c.id)) : cars} token={token} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
      {addOpen && <AddCarModal token={token} onClose={() => setAddOpen(false)} onDone={(close) => { if (close) setAddOpen(false); load(); }} />}
      {!brand.is_default && (
        <footer className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 text-[11px] fl-muted flex items-center justify-between gap-2 fl-divider" data-testid="flotila-powered-by">
          <span>{brand.company_name}{brand.website && <> · <a href={brand.website} target="_blank" rel="noreferrer" className="underline">{brand.website.replace(/^https?:\/\//, "")}</a></>}</span>
          <span className="inline-flex items-center gap-1.5">powered by <img src={LOGO_IMG} alt="EffectDeal" className="w-4 h-4 object-contain" /> <a href="https://www.effectdeal.com" target="_blank" rel="noreferrer" className="font-semibold">EffectDeal</a></span>
        </footer>
      )}
    </div>
  );
};

export default FlotilaPage;
